/**
 * Get tenant setup status and message from payload
 * @param {Object} payload - The payload containing status and/or message
 * @param {string|null} defaultStatus - Default status if not in payload
 * @param {string} currentMessage - Current message to use as fallback
 * @returns {Object} Object containing status and message
 */
export function getTenantSetupStatusFromPayload(
  payload,
  defaultStatus = null,
  currentMessage = '',
) {
  const status = payload?.status || defaultStatus
  const message = payload?.message

  let finalMessage = currentMessage

  if (message) {
    finalMessage = message
  } else if (status) {
    const statusMessages = {
      queued: "We're preparing your institute setup...",
      migrating: 'Setting up your workspace...',
      migrated: 'Almost there! Adding the finishing touches...',
      seeding: 'Creating your account and preparing your dashboard...',
      completed: 'All set! Your institute is ready to go.',
      failed: 'Oops! Something went wrong. Please contact support for assistance.',
    }
    finalMessage = statusMessages[status] || currentMessage
  }

  return {
    status,
    message: finalMessage,
  }
}

/**
 * Check if tenant setup socket should connect
 * @param {Object} params - Connection parameters
 * @param {boolean} params.isOnTenantDomain - Whether on tenant domain
 * @param {string|null} params.tenantOtp - Tenant OTP
 * @param {string|null} params.tenantSetup - Tenant setup flag
 * @param {boolean} params.isLocalhost - Whether on localhost
 * @param {string|null} params.tenantSubDomain - Tenant subdomain
 * @returns {boolean} Whether socket should connect
 */
export function shouldConnectTenantSetupSocket({
  isOnTenantDomain,
  tenantOtp,
  tenantSetup,
  isLocalhost,
  tenantSubDomain,
}) {
  return Boolean(
    (isOnTenantDomain && tenantOtp && tenantSetup) ||
      (isLocalhost && tenantOtp && tenantSetup && tenantSubDomain),
  )
}

/**
 * Get tenant setup socket handlers configuration
 * @param {Object} callbacks - Callback functions for handling socket events
 * @param {Function} callbacks.updateSetupStatusFromPayload - Function to update setup status
 * @param {Function} callbacks.showSuccessToast - Function to show success toast
 * @param {Function} callbacks.clearStatus - Function to clear status
 * @param {Function} callbacks.clearQueryParams - Function to clear query parameters
 * @param {Function} callbacks.setQueuedStatus - Function to set queued status with custom message
 * @returns {Object} Socket handlers configuration
 */
export function getTenantSetupSocketHandlers({
  updateSetupStatusFromPayload,
  showSuccessToast,
  clearStatus,
  clearQueryParams,
  setQueuedStatus,
}) {
  return {
    onJoin: (resp) => {
      if (resp?.status) {
        updateSetupStatusFromPayload(resp)
        if (resp.status === 'completed') {
          showSuccessToast()
          setTimeout(() => {
            clearStatus()
          }, 2000)
        } else if (resp.status === 'failed') {
          setTimeout(() => {
            clearStatus()
          }, 5000)
        }
      } else {
        if (setQueuedStatus) {
          setQueuedStatus()
        } else {
          updateSetupStatusFromPayload({ status: 'queued' }, 'queued')
        }
      }
    },
    onError: (error) => {},
    onQueued: (payload) => {
      updateSetupStatusFromPayload(payload, 'queued')
    },
    onMigrationStarted: (payload) => {
      updateSetupStatusFromPayload(payload, 'migrating')
    },
    onMigrationCompleted: (payload) => {
      updateSetupStatusFromPayload(payload, 'migrated')
    },
    onSeedingStarted: (payload) => {
      updateSetupStatusFromPayload(payload, 'seeding')
    },
    onCompleted: (payload) => {
      updateSetupStatusFromPayload(payload, 'completed')
      showSuccessToast()
      setTimeout(() => {
        clearQueryParams()
        clearStatus()
      }, 2000)
    },
    onFailed: (payload) => {
      updateSetupStatusFromPayload(payload, 'failed')
      setTimeout(() => {
        clearStatus()
      }, 5000)
    },
  }
}
